import {
  PermissionEnum,
  User as UserDto,
} from '@electricjs/core_entity-client';
import { Auth0User, Auth0UserKey } from '@externalTypes/auth0';

import { mockDivisions } from '@/mocks/data/divisionData';
import { simpleEmployee } from '@/mocks/data/employeeData';
import { simpleOrganization } from '@/mocks/data/organizationData';
import type { CallerIdentityWithDivisionsDto } from '@/redux/slices/userApiSlice';
import type { Division } from '@/types/divisions';
import { ElectricPermission, OrganizationUserRoleSlugs } from '@/types/users';

export const simpleUser: UserDto = {
  id: 'simple-user-id',
  email: 'simple-user@simple-org.com',
  auth0_id: 'simple-auth0-id',
};

export const simpleCallerIdentity: CallerIdentityWithDivisionsDto = {
  id: simpleUser.id,
  email: simpleUser.email,
  organizations: {
    ['simple-org-id']: {
      employee_id: 'simple-employee-id',
      organization_has_divisions: true,
      divisions: mockDivisions as Division[],
      roles: [
        {
          id: 'simple-role-id',
          name: 'simple-role-name',
          slug: OrganizationUserRoleSlugs.HUB_USER,
        },
        {
          id: 'simple-role-id',
          name: 'simple-role-name',
          slug: OrganizationUserRoleSlugs.SUPER_ADMIN,
        },
      ],
      permissions: [
        PermissionEnum.Applicationscreate,
        PermissionEnum.Employeesread,
        PermissionEnum.Integrationscreate,
        // Required for /offboardings/new and /onboardings/new routes.
        PermissionEnum.Employeesupdate,
        // Required for the Settings > Billing nav item to be visible.
        PermissionEnum.OrganizationPackageread,
        PermissionEnum.OrganizationAddOnsread,
        // Used across the app (shipments, warehousing, webhooks, assets, devices...).
        PermissionEnum.Assetscreate,
        PermissionEnum.Assetsread,
        PermissionEnum.Assetsupdate,
        PermissionEnum.Assetsdelete,
        PermissionEnum.Assetslocationread,
        PermissionEnum.Assetslocationupdate,
        PermissionEnum.Billingread,
        PermissionEnum.Devicesread,
        PermissionEnum.Deviceslock,
        PermissionEnum.Devicesunenroll,
        PermissionEnum.Deviceswipe,
        PermissionEnum.Employeescreate,
        PermissionEnum.HelpfulLinkscreate,
        PermissionEnum.HelpfulLinksupdate,
        PermissionEnum.HelpfulLinksdelete,
        PermissionEnum.Offboardingsread,
        PermissionEnum.Onboardingsread,
        PermissionEnum.Onboardingswrite,
        PermissionEnum.OrganizationAddOnscreate,
        PermissionEnum.OrganizationAddOnsupdate,
        PermissionEnum.OrganizationPackageupdate,
        PermissionEnum.Organizationsread,
        PermissionEnum.Organizationsupdate,
        PermissionEnum.Shipmentsread,
        PermissionEnum.Sitesdelete,
        PermissionEnum.WarrantyUpgradePreferenceread,
        PermissionEnum.WarrantyUpgradePreferenceupdate,
        PermissionEnum.Warehousingcreate,
        PermissionEnum.Warehousingread,
        PermissionEnum.Warehousingupdate,
        PermissionEnum.Webhooksread,
        PermissionEnum.Webhookswrite,
      ],
      hris_integrations: [
        {
          id: '6695cb87-899d-4d3d-8a44-b872b01872e6',
          hris_provider: {
            id: '6695cb87-899d-4d3d-8a44-b872b01872e6',
            name: 'BambooHR',
            slug: 'bamboohr',
          },
        },
      ],
    },
  },
};

// `/v1/users/me/identities` entries mirror the caller identity, with each org
// also carrying a `name` (the client package doesn't type this endpoint yet).
export const simpleUserIdentities = [
  {
    id: simpleCallerIdentity.id,
    email: simpleCallerIdentity.email,
    user_language: null,
    organizations: Object.fromEntries(
      Object.entries(simpleCallerIdentity.organizations).map(
        ([organizationId, organization]) => [
          organizationId,
          { ...organization, name: simpleOrganization.name },
        ]
      )
    ),
  },
];

// This auth0SimpleUser was primarily created to simulate a user on unit/integration tests
export const auth0SimpleUser: Auth0User = {
  email: simpleCallerIdentity.email,
  email_verified: true,
  name: `${simpleEmployee.first_name} ${simpleEmployee.last_name}`,
  nickname: `${simpleEmployee.first_name}.${simpleEmployee.last_name}`,
  [Auth0UserKey.IS_USER]: true,
  picture: '',
  [Auth0UserKey.ELECTRIC_PERMISSIONS]: [
    ElectricPermission.APPLICATION_READ,
    ElectricPermission.APPLICATION_UPDATE,
    ElectricPermission.APPLICATION_CREATE,
    ElectricPermission.APPLICATION_DELETE,
    ElectricPermission.ORDERS_READ,
    ElectricPermission.ORDERS_UPDATE,
    ElectricPermission.ORDERS_ISSUE_REFUND,
    ElectricPermission.ORGANIZATION_CREATE,
    ElectricPermission.PRODUCTS_READ,
    ElectricPermission.PRODUCTS_UPDATE,
  ],
  [Auth0UserKey.ORGANIZATIONS]: [simpleOrganization.id],
  [`${Auth0UserKey.ORGANIZATIONS}/${simpleOrganization.id}/employee_id`]:
    simpleEmployee.id,
};
